import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { REPORT_MAPPINGS } from 'dashboard/lib/constants/report-mappings';
import { WatchlistType } from 'dashboard/models/report/watchlist';
import {
  CustomListConfigHeaderMapping,
  ReportTemplateVersionAttributesAdverseMedia,
  ReportTemplateVersionAttributesBusinessAdverseMedia,
  ReportTemplateVersionAttributesBusinessAssociatedPersons,
  ReportTemplateVersionAttributesBusinessWatchlist,
  ReportTemplateVersionAttributesPoliticallyExposedPerson,
  ReportTemplateVersionAttributesScreening,
  ReportTemplateVersionAttributesWatchlist,
  ReportTemplateVersionLegacy,
} from 'dashboard/models/report-template-version';
import { MatchGroup } from 'dashboard/models/verification-check';
import { WorkflowStepActionKey } from 'dashboard/models/workflow-version';
import {
  createPreviewReportAddressLookup,
  createPreviewReportAdverseMedia,
  createPreviewReportBusinessAdverseMedia,
  createPreviewReportBusinessWatchlist,
  createPreviewReportCryptoAddressWatchlist,
  createPreviewReportEmailAddress,
  createPreviewReportPhoneNumber,
  createPreviewReportPoliticallyExposedPersonUpdated,
  createPreviewReportWatchlistUpdated,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/report';
import {
  createPreviewReportTemplateAddressLookup,
  createPreviewReportTemplateAdverseMedia,
  createPreviewReportTemplateBusinessAdverseMedia,
  createPreviewReportTemplateBusinessWatchlist,
  createPreviewReportTemplateCryptoAddressWatchlist,
  createPreviewReportTemplateEmailAddress,
  createPreviewReportTemplatePhoneNumber,
  createPreviewReportTemplatePoliticallyExposedPerson,
  createPreviewReportTemplateWatchlist,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/report-template';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

import { ProductFeatureName } from './organization';

export enum ReportTemplateStatus {
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
}

export interface ReportTemplateAttributes<E = unknown> {
  name: string;
  archived: boolean;
  status?: ReportTemplateStatus;
  'updated-at': string;
  'archived-at'?: string;
  'latest-published-version'?: ReportTemplateVersionLegacy<E>;
}

export interface ReportTemplateFilterOption {
  id: string;
  name: string;
  type: ReportTemplate['type'];
  status?: ReportTemplateStatus;
  archived: boolean;
  'updated-at': string;
}

export interface ReportTemplateFiltersMeta {
  filters: ReportTemplateFilterOption[];
}

export interface ReportTemplateFiltersResponse {
  meta: ReportTemplateFiltersMeta;
}

export interface CustomListVersionHistory {
  id: string;
  name: string;
  'created-at': string;
  description?: string;
  'uploaded-at': string;
  'file-name': string;
}

export interface CustomListRunHistory {
  'creator-name': string;
  description: string;
  'created-at': string;
  'completed-at': string;
  'environment-token': string;
  'report-template-version-token': string;
  status: 'not_started' | 'queued' | 'running' | 'complete' | 'failed';
}

export interface ReportTemplateAttributesCustomList extends ReportTemplateAttributes {
  'version-history': CustomListVersionHistory[];
  'run-history': CustomListRunHistory[];
}

export interface ReportTemplateAttributesWatchlist extends ReportTemplateAttributes {
  'data-sources-persona'?: boolean;
  'exact-match-country'?: boolean;
  'latest-published-version'?: ReportTemplateVersionLegacy<ReportTemplateVersionAttributesWatchlist>;
}

export interface ReportTemplateCustomListConfigFormData {
  name?: string;
  'custom-list-file'?: FileWithPreview;
  'change-description': string;
  'header-mappings'?: CustomListConfigHeaderMapping[];
  'match-groups'?: MatchGroup[];
}

export interface ReportTemplateRelationships<E extends ApiModel> {
  'latest-published-version'?: ApiRelationship<E>;
  'report-template-versions'?: ApiListRelationship<E>;
}

export interface ReportTemplateWatchlistMeta {
  sources?: WatchlistSourceList[];
  tags?: WatchlistTag[];
}
export interface WatchlistSourceList {
  category: WatchlistType;
  'flag-code': string;
  tags?: string[];
  slug: string;
  name: string;
  status?: string;
  source: string;
}

export interface WatchlistTag {
  category: string;
  'flag-code': string;
  slug: string;
  name: string;
  lists: string[];
}

export enum ReportBuilderName {
  AddressLookup = 'addressLookup',
  AdverseMedia = 'adverseMedia',
  BusinessAdverseMedia = 'businessAdverseMedia',
  BusinessWatchlist = 'businessWatchlist',
  CryptoAddressWatchlist = 'cryptoAddressWatchlist',
  EmailAddress = 'emailAddress',
  PhoneNumber = 'phoneNumber',
  PoliticallyExposedPerson = 'politicallyExposedPerson',
  Watchlist = 'watchlist',
}

export const REPORT_BUILDER_NAME_TO_ENDPOINT: Record<string, ApiModel> = {
  [ReportBuilderName.AddressLookup]: ApiModel.ReportTemplateAddressLookup,
  [ReportBuilderName.AdverseMedia]: ApiModel.ReportTemplateAdverseMedia,
  [ReportBuilderName.CryptoAddressWatchlist]: ApiModel.ReportTemplateCryptoAddressWatchlist,
  [ReportBuilderName.EmailAddress]: ApiModel.ReportTemplateEmailAddress,
  [ReportBuilderName.PhoneNumber]: ApiModel.ReportTemplatePhoneNumber,
  [ReportBuilderName.PoliticallyExposedPerson]: ApiModel.ReportTemplatePoliticallyExposedPerson,
  [ReportBuilderName.Watchlist]: ApiModel.ReportTemplateWatchlist,
};

export interface ReportTemplateBuilder {
  name: ReportBuilderName;
  reportTypes: ReportTemplate['type'][];
  requiredProductFeatures: ProductFeatureName[];
  requiresOrderForm: boolean;
}

const reportTemplateAddressLookup = createPreviewReportTemplateAddressLookup();
const reportAddressLookup = createPreviewReportAddressLookup(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateAddressLookup.id,
        type: ApiModel.ReportTemplateAddressLookup,
      },
    },
  },
);

const reportTemplateAdverseMedia = createPreviewReportTemplateAdverseMedia();
const reportAdverseMedia = createPreviewReportAdverseMedia(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateAdverseMedia.id,
        type: ApiModel.ReportTemplateAdverseMedia,
      },
    },
  },
);

const reportTemplateBusinessAdverseMedia = createPreviewReportTemplateBusinessAdverseMedia();
const reportBusinessAdverseMedia = createPreviewReportBusinessAdverseMedia(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessAdverseMedia.id,
        type: ApiModel.ReportTemplateBusinessAdverseMedia,
      },
    },
  },
);

const reportTemplateBusinessWatchlist = createPreviewReportTemplateBusinessWatchlist();
const reportBusinessWatchlist = createPreviewReportBusinessWatchlist(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessWatchlist.id,
        type: ApiModel.ReportTemplateBusinessWatchlist,
      },
    },
  },
);

const reportTemplateCryptoAddressWatchlist = createPreviewReportTemplateCryptoAddressWatchlist();
const reportCryptoAddressWatchlist = createPreviewReportCryptoAddressWatchlist(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateCryptoAddressWatchlist.id,
        type: ApiModel.ReportTemplateCryptoAddressWatchlist,
      },
    },
  },
);

const reportTemplateEmailAddress = createPreviewReportTemplateEmailAddress();
const reportEmailAddress = createPreviewReportEmailAddress(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateEmailAddress.id,
        type: ApiModel.ReportTemplateEmailAddress,
      },
    },
  },
);

const reportTemplatePhoneNumber = createPreviewReportTemplatePhoneNumber();
const reportPhoneNumber = createPreviewReportPhoneNumber(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplatePhoneNumber.id,
        type: ApiModel.ReportTemplatePhoneNumber,
      },
    },
  },
);

const reportTemplatePoliticallyExposedPerson =
  createPreviewReportTemplatePoliticallyExposedPerson();
const reportPoliticallyExposedPerson = createPreviewReportPoliticallyExposedPersonUpdated(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplatePoliticallyExposedPerson.id,
        type: ApiModel.ReportTemplatePoliticallyExposedPerson,
      },
    },
  },
);

const reportTemplateWatchlist = createPreviewReportTemplateWatchlist();
const reportWatchlist = createPreviewReportWatchlistUpdated(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateWatchlist.id,
        type: ApiModel.ReportTemplateWatchlist,
      },
    },
  },
);

export const REPORT_BUILDER_NAME_TO_PREVIEW: Record<string, any> = {
  [ReportBuilderName.AddressLookup]: reportAddressLookup,
  [ReportBuilderName.AdverseMedia]: reportAdverseMedia,
  [ReportBuilderName.BusinessAdverseMedia]: reportBusinessAdverseMedia,
  [ReportBuilderName.BusinessWatchlist]: reportBusinessWatchlist,
  [ReportBuilderName.CryptoAddressWatchlist]: reportCryptoAddressWatchlist,
  [ReportBuilderName.EmailAddress]: reportEmailAddress,
  [ReportBuilderName.PhoneNumber]: reportPhoneNumber,
  [ReportBuilderName.PoliticallyExposedPerson]: reportPoliticallyExposedPerson,
  [ReportBuilderName.Watchlist]: reportWatchlist,
};

export type ReportTemplateAddressLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionAddressLookup>,
  ApiModel.ReportTemplateAddressLookup
>;

export type ReportTemplateAdverseMedia = ApiResource<
  ReportTemplateAttributes<ReportTemplateVersionAttributesAdverseMedia>,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionAdverseMedia>,
  ApiModel.ReportTemplateAdverseMedia
>;

export type ReportTemplateBusinessAdverseMedia = ApiResource<
  ReportTemplateAttributes<ReportTemplateVersionAttributesBusinessAdverseMedia>,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessAdverseMedia>,
  ApiModel.ReportTemplateBusinessAdverseMedia
>;

export type ReportTemplateBusinessAssociatedPersons = ApiResource<
  ReportTemplateAttributes<ReportTemplateVersionAttributesBusinessAssociatedPersons>,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessAssociatedPersons>,
  ApiModel.ReportTemplateBusinessAssociatedPersons
>;

export type ReportTemplateBusinessClassification = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessClassification>,
  ApiModel.ReportTemplateBusinessClassification
>;

export type ReportTemplateBusinessIndustryClassification = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessIndustryClassification>,
  ApiModel.ReportTemplateBusinessIndustryClassification
>;

export type ReportTemplateBusinessLiensLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessLiensLookup>,
  ApiModel.ReportTemplateBusinessLiensLookup
>;

export type ReportTemplateBusinessLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessLookup>,
  ApiModel.ReportTemplateBusinessLookup
>;

export type ReportTemplateBusinessNonprofit = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessNonprofit>,
  ApiModel.ReportTemplateBusinessNonprofit
>;

export type ReportTemplateBusinessOnlinePresence = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessOnlinePresence>,
  ApiModel.ReportTemplateBusinessOnlinePresence
>;

export type ReportTemplateBusinessRegistrationsLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessRegistrationsLookup>,
  ApiModel.ReportTemplateBusinessRegistrationsLookup
>;

export type ReportTemplateBusinessSocialMedia = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessSocialMedia>,
  ApiModel.ReportTemplateBusinessSocialMedia
>;

export type ReportTemplateBusinessWatchlist = ApiResource<
  ReportTemplateAttributes<ReportTemplateVersionAttributesBusinessWatchlist>,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessWatchlist>,
  ApiModel.ReportTemplateBusinessWatchlist
>;

export type ReportTemplateBusinessWebsite = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionBusinessWebsite>,
  ApiModel.ReportTemplateBusinessWebsite
>;

export type ReportTemplateChainalysisAddressScreening = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionChainalysisAddressScreening>,
  ApiModel.ReportTemplateChainalysisAddressScreening
>;

export type ReportTemplateClearbitBusinessLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionClearbitBusinessLookup>,
  ApiModel.ReportTemplateClearbitBusinessLookup
>;

export type ReportTemplateCoinbaseCheckCryptoRisk = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateCoinbaseCheckCryptoRisk>,
  ApiModel.ReportTemplateCoinbaseCheckCryptoRisk
>;

export type ReportTemplateCryptoAddressWatchlist = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionCryptoAddressWatchlist>,
  ApiModel.ReportTemplateCryptoAddressWatchlist
>;

export type ReportTemplateCustomList = ApiResource<
  ReportTemplateAttributesCustomList,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionCustomList>,
  ApiModel.ReportTemplateCustomList
>;

export type ReportTemplateEmailAddress = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionEmailAddress>,
  ApiModel.ReportTemplateEmailAddress
>;

export type ReportTemplateEquifaxOneview = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionEquifaxOneview>,
  ApiModel.ReportTemplateEquifaxOneview
>;

export type ReportTemplateFinraBrokerCheck = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionFinraBrokerCheck>,
  ApiModel.ReportTemplateFinraBrokerCheck
>;

export type ReportTemplateKyckrBusinessLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionKyckrBusinessLookup>,
  ApiModel.ReportTemplateKyckrBusinessLookup
>;

export type ReportTemplateMiddeskBusinessLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionMiddeskBusinessLookup>,
  ApiModel.ReportTemplateMiddeskBusinessLookup
>;

export type ReportTemplateMxAccount = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionMxAccount>,
  ApiModel.ReportTemplateMxAccount
>;

export type ReportTemplatePhoneNumber = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionPhoneNumber>,
  ApiModel.ReportTemplatePhoneNumber
>;

export type ReportTemplatePoliticallyExposedPerson = ApiResource<
  ReportTemplateAttributes<ReportTemplateVersionAttributesPoliticallyExposedPerson>,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionPoliticallyExposedPerson>,
  ApiModel.ReportTemplatePoliticallyExposedPerson
>;

export type ReportTemplateProfile = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionProfile>,
  ApiModel.ReportTemplateProfile
>;

export type ReportTemplateProfileNonAuthoritative = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionProfileNonAuthoritative>,
  ApiModel.ReportTemplateProfileNonAuthoritative
>;

export type ReportTemplateScreening = ApiResource<
  ReportTemplateAttributes<ReportTemplateVersionAttributesScreening>,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionScreening>,
  ApiModel.ReportTemplateScreening
>;

export type ReportTemplateSecActionLookup = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionSecActionLookup>,
  ApiModel.ReportTemplateSecActionLookup
>;

export type ReportTemplateSentilinkApplicationRisk = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionSentilinkApplicationRisk>,
  ApiModel.ReportTemplateSentilinkApplicationRisk
>;

export type ReportTemplateSentilinkScores = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionSentilinkScores>,
  ApiModel.ReportTemplateSentilinkScores
>;

export type ReportTemplateSocialMedia = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionSocialMedia>,
  ApiModel.ReportTemplateSocialMedia
>;

export type ReportTemplateSynthetic = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionSynthetic>,
  ApiModel.ReportTemplateSynthetic
>;

export type ReportTemplateTrmWalletScreening = ApiResource<
  ReportTemplateAttributes,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionTrmWalletScreening>,
  ApiModel.ReportTemplateTrmWalletScreening
>;

export type ReportTemplateWatchlist = ApiResource<
  ReportTemplateAttributesWatchlist,
  ReportTemplateRelationships<ApiModel.ReportTemplateVersionWatchlist>,
  ApiModel.ReportTemplateWatchlist
>;

export type ReportTemplate =
  | ReportTemplateAddressLookup
  | ReportTemplateAdverseMedia
  | ReportTemplateBusinessAdverseMedia
  | ReportTemplateBusinessAssociatedPersons
  | ReportTemplateBusinessClassification
  | ReportTemplateBusinessIndustryClassification
  | ReportTemplateBusinessLiensLookup
  | ReportTemplateBusinessLookup
  | ReportTemplateBusinessNonprofit
  | ReportTemplateBusinessOnlinePresence
  | ReportTemplateBusinessRegistrationsLookup
  | ReportTemplateBusinessSocialMedia
  | ReportTemplateBusinessWatchlist
  | ReportTemplateBusinessWebsite
  | ReportTemplateChainalysisAddressScreening
  | ReportTemplateClearbitBusinessLookup
  | ReportTemplateCoinbaseCheckCryptoRisk
  | ReportTemplateCryptoAddressWatchlist
  | ReportTemplateCustomList
  | ReportTemplateEmailAddress
  | ReportTemplateEquifaxOneview
  | ReportTemplateFinraBrokerCheck
  | ReportTemplateKyckrBusinessLookup
  | ReportTemplateMiddeskBusinessLookup
  | ReportTemplateMxAccount
  | ReportTemplatePhoneNumber
  | ReportTemplatePoliticallyExposedPerson
  | ReportTemplateProfile
  | ReportTemplateProfileNonAuthoritative
  | ReportTemplateScreening
  | ReportTemplateSecActionLookup
  | ReportTemplateSentilinkApplicationRisk
  | ReportTemplateSentilinkScores
  | ReportTemplateSocialMedia
  | ReportTemplateSynthetic
  | ReportTemplateTrmWalletScreening
  | ReportTemplateWatchlist;

export const SUPPORTED_BULK_UPLOAD_REPORT_TEMPLATE_TYPES = [
  ApiModel.ReportTemplateAddressLookup,
  ApiModel.ReportTemplateAdverseMedia,
  ApiModel.ReportTemplateBusinessAdverseMedia,
  ApiModel.ReportTemplateBusinessLookup,
  ApiModel.ReportTemplateBusinessWatchlist,
  ApiModel.ReportTemplateEmailAddress,
  ApiModel.ReportTemplatePhoneNumber,
  ApiModel.ReportTemplatePoliticallyExposedPerson,
  ApiModel.ReportTemplateProfileNonAuthoritative,
  ApiModel.ReportTemplateWatchlist,
];

export const REPORT_TEMPLATE_TYPE_TO_FIELDS: Record<ReportTemplate['type'], string[]> =
  Object.fromEntries(REPORT_MAPPINGS.map((r) => [[r.reportTemplateType], r.fields]));

export const REPORT_TEMPLATE_TYPE_TO_LABEL: Record<ReportTemplate['type'], string> =
  Object.fromEntries(REPORT_MAPPINGS.map((r) => [[r.reportTemplateType], r.label]));

export const REPORT_TEMPLATE_TYPE_TO_WORKFLOW_ACTION: Record<
  ReportTemplate['type'],
  WorkflowStepActionKey
> = Object.fromEntries(
  // Filter out report templates that don't have a workflow step action key
  // because that means they are not creatable via workflows
  REPORT_MAPPINGS.filter((r) => r.workflowStepActionKey != null).map((r) => [
    [r.reportTemplateType],
    r.workflowStepActionKey,
  ]),
);
